import {QueryResult, useLazyQuery, QueryHookOptions, type LazyQueryExecFunction, type OperationVariables} from "@apollo/client"
import API from "@irati/utils/services/API/utils/errorHandler/register"
import {get} from "lodash"
import {
  ApolloLoad,
  DocumentNode,
  ServiceOptions,
  deriveHookNameAndRenameFunctionForDevtools,
  type PBApolloError
} from "./helpers/hookShared"

type UseLazyQueryResult<A, B extends OperationVariables> = [
  LazyQueryExecFunction<A, B>,
  Prettify<
    Pick<QueryResult<A, B>, "loading" | "networkStatus" | "called" | "refetch" | "variables" | "fetchMore" | "updateQuery"> & {
      /** Data from the request */
      data: A | undefined
      /**
       * An error object from the failing service
       * @deprecated please use `errorMsg`
       */
      error?: PBApolloError
      errorMsg?: string
      /** A pending promise that can be passed to `<Loading />` components or collected in array.
       *
       * @example
       * function MyComp() {
       *   const [getReport, {load, data}] = useReportQuery()
       *
       *   return (
       *     <Button onClick={getReport}>Download report</Button>
       *     <Loading load={load}>
       *       {data}
       *     </Loading>
       *   )
       * }
       */
      load: ApolloLoad
    }
  >
]

/**
 * Stripped down variables to pass to useLazyQuery
 * More can be added if identified as needed, but minimsing the api surface for now
 */
type UseLazyQueryOptions<A, B> = Prettify<
  Pick<QueryHookOptions<A, B>, "fetchPolicy" | "pollInterval" | "partialRefetch" | "notifyOnNetworkStatusChange">
>

/**
 * Create a lazy query hook for reuse throughout the codebase. This is highly recomended over call `useLazyQuery` directly.
 *
 * The benifit of using this factory function is to get strongly typed values, and marshal the output into more commonly used formats,
 * not limited to but including a `load` value for passing to `Loading` components
 *
 * For details of the underlying `useLazyQuery` see [apollo client](https://www.apollographql.com/docs/react/api/react/hooks/#uselazyquery)
 *
 * @example
 * const useLazyUsersForBusiness = createUseLazyQuery<UserList, QueryUsersForBusinessArgs>(getUsersForBusiness, {
 *  key: "usersForBusiness"
 * })
 *
 * function Users({ userIds }) {
 *   const [fetchUsers, apollo] = useLazyUsersForBusiness({ userIds })
 *   return (</>
 *     <p>users<p>
 *     <button onClick={fetchUsers}>fetch</button>
 *     <Loading load={apollo.load} />
 *     <pre>{apollo.data}</pre>
 *   </>)
 * }
 */
export function createUseLazyQuery<A, B>(
  query: DocumentNode,
  {key, errorHandlers = [], ...useQueryOptions}: Prettify<ServiceOptions & UseLazyQueryOptions<A, B>>
) {
  errorHandlers.forEach((handler) => {
    API.add(handler)
  })

  function useHook(variables: B, options: UseLazyQueryOptions<A, B> = {}): UseLazyQueryResult<A, B> {
    const [queryFn, apollo] = useLazyQuery<A, B>(query, {
      variables,
      context: useQueryOptions.forceEU ? {reqEUinstance: true} : {},
      ...useQueryOptions,
      ...options
    })

    // Here we are overriding the error type to be a PB Error as we know this will be handled by our client
    const error = apollo.error as PBApolloError | undefined

    // here we assert the key index will be of type `A` - don't assume it should be
    // `keyof A` because we often define types in terms of the nested graphql type, not the root type
    const data = key && apollo.data ? (get(apollo.data as any, key) as A) : apollo.data

    return [
      queryFn,
      {
        ...apollo,
        refetch: async (arg) => {
          const res = await apollo.refetch()
          return key && res.data ? {...res, data: get(res.data, key)} : res
        },
        error,
        errorMsg: error?.networkError?.formattedMessage ?? error?.message,
        load: {
          ...apollo,
          error,
          __brand: "ApolloLoad"
        },
        data
      }
    ]
  }

  deriveHookNameAndRenameFunctionForDevtools(useHook, "Query", query)
  return useHook
}
